import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, tap } from 'rxjs';
import { BaseRespone, PaginationResult } from 'src/app/models/common.response';
import { PaginationRequest } from 'src/app/models/common.request';
import { environment } from 'src/environments/environment';
import {
  DrugInventoryItem,
  DrugInventoryLookup,
  DrugBatchItem,
  PharmacyBillCommand,
  PharmacyBillResult,
  BulkStockInCommand,
  BulkStockInResult,
  PagedDrugInventoryResult,
  PagedPharmacyBillListResult,
  PagedStockInListResult,
} from 'src/app/models/pharmacy.model';

@Injectable({ providedIn: 'root' })
export class PharmacyService {

  private _inventory: BehaviorSubject<DrugInventoryItem[]> = new BehaviorSubject<DrugInventoryItem[]>([]);
  private _pagination: BehaviorSubject<PaginationResult | null> = new BehaviorSubject<PaginationResult | null>(null);

  constructor(private _httpClient: HttpClient) {}

  get inventory$(): Observable<DrugInventoryItem[]> {
    return this._inventory.asObservable();
  }

  get pagination$(): Observable<PaginationResult | null> {
    return this._pagination.asObservable();
  }

  getAllInventory(pagination: PaginationRequest): Observable<BaseRespone<PagedDrugInventoryResult>> {
    return this._httpClient.post<BaseRespone<PagedDrugInventoryResult>>(
      `${environment.baseURl}/Pharmacy/inventory/list`, pagination
    ).pipe(
      tap((response) => {
        this._pagination.next(response.result.pagination as any);
        this._inventory.next(response.result.items);
      }),
      catchError((error) => {
        console.error('Error fetching inventory:', error);
        throw error;
      })
    );
  }

  addInventory(item: Partial<DrugInventoryItem>): Observable<BaseRespone<DrugInventoryItem>> {
    return this._httpClient.post<BaseRespone<DrugInventoryItem>>(
      `${environment.baseURl}/Pharmacy/inventory`, item
    ).pipe(
      catchError((error) => {
        console.error('Error adding inventory:', error);
        throw error;
      })
    );
  }

  updateInventory(item: Partial<DrugInventoryItem>): Observable<BaseRespone<DrugInventoryItem>> {
    return this._httpClient.put<BaseRespone<DrugInventoryItem>>(
      `${environment.baseURl}/Pharmacy/inventory`, item
    ).pipe(
      catchError((error) => {
        console.error('Error updating inventory:', error);
        throw error;
      })
    );
  }

  getInventoryLookup(): Observable<BaseRespone<DrugInventoryLookup[]>> {
    return this._httpClient.get<BaseRespone<DrugInventoryLookup[]>>(
      `${environment.baseURl}/Pharmacy/inventory/lookup`
    ).pipe(
      catchError((error) => {
        console.error('Error fetching inventory lookup:', error);
        throw error;
      })
    );
  }

  getBatches(drugInventoryId: number): Observable<BaseRespone<DrugBatchItem[]>> {
    return this._httpClient.get<BaseRespone<DrugBatchItem[]>>(
      `${environment.baseURl}/Pharmacy/batch/${drugInventoryId}`
    ).pipe(
      catchError((error) => {
        console.error('Error fetching batches:', error);
        throw error;
      })
    );
  }

  addBatch(command: Partial<DrugBatchItem>): Observable<BaseRespone<DrugBatchItem>> {
    return this._httpClient.post<BaseRespone<DrugBatchItem>>(
      `${environment.baseURl}/Pharmacy/batch`, command
    ).pipe(
      catchError((error) => {
        console.error('Error adding batch:', error);
        throw error;
      })
    );
  }

  bulkStockIn(command: BulkStockInCommand): Observable<BaseRespone<BulkStockInResult>> {
    return this._httpClient.post<BaseRespone<BulkStockInResult>>(
      `${environment.baseURl}/Pharmacy/stock-in`, command
    ).pipe(
      catchError((error) => {
        console.error('Error processing stock in:', error);
        throw error;
      })
    );
  }

  dispense(command: PharmacyBillCommand): Observable<BaseRespone<PharmacyBillResult>> {
    return this._httpClient.post<BaseRespone<PharmacyBillResult>>(
      `${environment.baseURl}/Pharmacy/dispense`, command
    ).pipe(
      catchError((error) => {
        console.error('Error dispensing bill:', error);
        throw error;
      })
    );
  }

  getPharmacyBills(query: { startDate: string; endDate: string; searchValue?: string; pageNumber: number; pageSize: number }): Observable<BaseRespone<PagedPharmacyBillListResult>> {
    return this._httpClient.post<BaseRespone<PagedPharmacyBillListResult>>(
      `${environment.baseURl}/Pharmacy/bills`, query
    );
  }

  getPharmacyBillById(id: number): Observable<BaseRespone<PharmacyBillResult>> {
    return this._httpClient.get<BaseRespone<PharmacyBillResult>>(
      `${environment.baseURl}/Pharmacy/bill/${id}`
    );
  }

  getStockInList(query: { startDate: string; endDate: string; searchValue?: string; pageNumber: number; pageSize: number }): Observable<BaseRespone<PagedStockInListResult>> {
    return this._httpClient.post<BaseRespone<PagedStockInListResult>>(
      `${environment.baseURl}/Pharmacy/stock-in/list`, query
    );
  }
}
